"use client"
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  Paper,
  Stack,
  alpha,
  Link,
} from "@mui/material"
import PaymentIcon from "@mui/icons-material/Payment"
import AutorenewIcon from "@mui/icons-material/Autorenew"
import CancelIcon from "@mui/icons-material/Cancel"
import SettingsIcon from "@mui/icons-material/Settings"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import LaunchIcon from "@mui/icons-material/Launch"
import config from "../config"

const Pricing = () => {
  const theme = useTheme()


  // Handle Manage Subscription button click
  const handleManageSub = (event) => {
    event.preventDefault()
    window.open(config.UPGRADE_CHAT_LINK, "_blank", "noopener,noreferrer")
  }

  // Handle Buy Bot button click - navigate directly to the purchase page
  const handlePurchaseSub = () => {
    window.open("https://chat-tier.com/dark-edge", "_blank", "noopener,noreferrer")
  }

  return (
    <Container maxWidth="md">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: "rgba(13, 15, 25, 0.7)",
          border: "1px solid rgba(0, 229, 255, 0.1)",
          borderRadius: 2,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0.03,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='smallGrid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='%2300e5ff' strokeWidth='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23smallGrid)'/%3E%3C/svg%3E\")",
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
            <PaymentIcon
              sx={{
                fontSize: 40,
                color: theme.palette.primary.main,
                textShadow: "0 0 8px rgba(0, 229, 255, 0.6)",
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                background: "linear-gradient(45deg, #ffffff 30%, #00e5ff 90%)",
                backgroundClip: "text",
                textFillColor: "transparent",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 0 2px rgba(0, 229, 255, 0.5)",
              }}
            >
              Buy Bot Information
            </Typography>
          </Box>

          {/* Move the list items directly into the outer container */}
          <Stack spacing={3} sx={{ mb: 4, maxWidth: "800px", mx: "auto" }}>
            {/* Auto-renewal info with manage link */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                background: "rgba(0, 0, 0, 0.3)",
              }}
            >
              <AutorenewIcon sx={{ color: theme.palette.info.main, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: theme.palette.info.main, mb: 0.5 }}>
                  Auto-Renewal
                </Typography>
                <Typography variant="body2">
                  Bot is subscription based. Subscriptions will automatically renew until cancelled. You can cancel at
                  any time via the{" "}
                  <Link
                    href="#"
                    onClick={handleManageSub}
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#00e5ff",
                      },
                    }}
                  >
                    Manage Subscription
                    <LaunchIcon sx={{ fontSize: "0.875rem" }} />
                  </Link>{" "}
                  page.
                </Typography>
              </Box>
            </Box>

            {/* Cancellation info with link on "cancel your subscription" */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                background: "rgba(0, 0, 0, 0.3)",
              }}
            >
              <CancelIcon sx={{ color: theme.palette.warning.main, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: theme.palette.warning.main, mb: 0.5 }}>
                  Cancellation
                </Typography>
                <Typography variant="body2">
                  If you{" "}
                  <Link
                    href="#"
                    onClick={handleManageSub}
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#00e5ff",
                      },
                    }}
                  >
                    cancel your subscription
                    <LaunchIcon sx={{ fontSize: "0.875rem" }} />
                  </Link>
                  , you will retain access until the end of your current billing period.
                </Typography>
              </Box>
            </Box>

            {/* Role reclaim info with link on "profile" */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                background: "rgba(0, 0, 0, 0.3)",
              }}
            >
              <SettingsIcon sx={{ color: theme.palette.primary.main, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, mb: 0.5 }}>
                  Lost Role?
                </Typography>
                <Typography variant="body2">
                  If you lose your role (e.g., leave the server), you can reclaim it from chat-tier by clicking the
                  gear icon in your{" "}
                  <Link
                    href="#"
                    onClick={handleManageSub}
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#00e5ff",
                      },
                    }}
                  >
                    profile
                    <LaunchIcon sx={{ fontSize: "0.875rem" }} />
                  </Link>{" "}
                  and selecting "Reclaim".
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePurchaseSub}
            startIcon={<ShoppingCartIcon />}
            size="large"
            sx={{
              background: "linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)",
              boxShadow: "0 0 10px rgba(0, 229, 255, 0.5)",
              px: 4,
              py: 1.5,
              "&:hover": {
                boxShadow: "0 0 15px rgba(0, 229, 255, 0.8)",
              },
            }}
          >
            Buy Bot
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default Pricing
